import React, { useRef } from 'react';
import { useState, useEffect, usePrintContext, Hooks, Form, Field, Title, Code, Actions, API, Utility, Button, Messages, Control } from 'helpers/PrintImports';
import { Release } from "models/ReleaseModel";
import { User } from 'models/UserModel';
import { Doctor } from 'models/DoctorModel';
import { Client } from 'models/ClientModel';
import { Class } from 'constants/codes';

const ReleasePrint: React.FC = () => {
  const width = { product: 42, price: 12, qty: 10, discount: 12, gst: 12, Amount: 12 }
  const [formId, formName] = [Code.Form.Id.RELEASE, Code.Form.Name.RELEASE];
  const {keyId, loading, setLoading, showMenu, setShowMenu, message, setMessage, error, setError, menuPosition, setMenuPosition, contextMenuRef } = usePrintContext();
  const [releaseData, setReleaseData] = useState<Release>({});
  const [clientData, setClientData] = useState<Client | null>(null);
  const [doctorData, setDoctorData] = useState<Doctor | null>(null);
  const [userData, setUserData] = useState<User | null>(null);

  const Privilege = Hooks.Privilege.List(formId);
  const printRef = useRef<HTMLDivElement>(null);
  Hooks.Privilege.HasPrint(Privilege);
  const companyId = Utility.Storage.CompanyId();
  const locationId = Utility.Storage.LocationId();
  const companyName = Utility.Storage.CompanyName();

  useEffect(() => {
    API.Data.GetByKeyId<Release>(formName, keyId, setLoading, setError)
      .then(async(data) => { if(data) {
      const formData = await Form.Map.Data(data);
      setReleaseData((prevData) => ({
        ...prevData,
        ...formData,
      }));
 
      setClientData(await API.Data.GetClient(data.client_id));
      setDoctorData(await API.Data.GetDoctor(data.doctor_id));
      setUserData(await API.Data.GetUser(data.user_id));
    }})
    setLoading(false);
  }, []); 

  return (
    <Control.Print.Frame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
      {!loading && (<>
        <Messages.Toast Messages={message} Error={error} />
        <Button.Print keyId={keyId} formName={formName} Privilege={Privilege} clientData={clientData} fileName={releaseData.release_number} docType={Code.Option.DocumentType.RELEASE} printRef={printRef} setMessage={setMessage} setError={setError} />
        <Control.Print.Data ref={printRef}>
            <Control.Flex>
              <Control.Column size={[0,60,0,0]}>
                <Control.Box>
                  <Field.CompanyHead companyId={companyId} locationId={locationId} />
                </Control.Box>
                <Control.Box>
                  <Field.Value value={Utility.Get.FullName(clientData, Code.Form.Name.CLIENT).toUpperCase()} valueBold={true} />
                  <Field.Value value={Utility.Format.Phone(clientData?.client_phone)} />
                  <Field.Value value={clientData?.client_address || ''} />
                  <Field.Value value={`${clientData?.city_name || ''} ${clientData?.client_postal?.toUpperCase() || ''}`} />
                </Control.Box>
              </Control.Column>
              <Control.Column size={[0,40,0,0]} align={Code.Align.RIGHT}>
                <Control.Box>
                  <Field.SubHead title={formName} />
                  <Control.Form.Barcode value={releaseData.release_number!} />       
                  <Field.Value title={Title.BillNo} value={releaseData.release_number} />
                  <Field.Value title={Title.BillDate} value={Utility.Format.DateOnly(releaseData.release_date)} />
                  <Field.Value title={Title.Doctor} value={Utility.Get.FullName(doctorData,Code.Form.Name.DOCTOR)} />
                  <Field.Value title={Title.Dispenser} value={Utility.Get.FullName(userData,Code.Form.Name.USER)} />
                  <Field.Value title={Title.Client} value={`#${clientData?.client_number}`} />
                  <Field.Value title={Title.DOB} value={Utility.Format.DateOnly(clientData?.client_dob)} />
                </Control.Box>
              </Control.Column>
            </Control.Flex>
            <Control.Print.Table>
              <Control.Print.Title>
                <Control.Grid.Items values={[
                  [Title.Desc, width.product], 
                  [Title.Price, width.price, Code.Align.RIGHT], 
                  [Title.Qty, width.qty, Code.Align.CENTER],
                  [Title.Disc, width.discount, Code.Align.RIGHT],
                  [Title.GST, width.gst, Code.Align.RIGHT],
                  [Title.Amount, width.Amount, Code.Align.RIGHT]
                ]} upper={true} />
              </Control.Print.Title>
              <Control.Print.List>
                {releaseData.items?.map((item, index) => (
                  <Control.Print.Index key={index} index={index}>
                    <Control.Print.Item>
                      <Control.Print.Product product={item.product_name} sku={item.item_skucode} comment={item.item_comment} size={[width.product]} />
                      <Control.Print.Number value={item.item_price} size={[width.price]}  />
                      <Control.Print.Text value={item.item_quantity} size={[width.qty]} align={Code.Align.CENTER} />
                      <Control.Print.Number value={item.item_discount_amount} size={[width.discount]} />
                      <Control.Print.Number value={item.item_tax1_amount} size={[width.gst]} />
                      <Control.Print.Number value={item.item_net_amount} size={[width.Amount]}  /> 
                    </Control.Print.Item>
                  </Control.Print.Index>
                ))}
              </Control.Print.List>
            </Control.Print.Table>
            <Control.Flex className={Class.ptop3}>
              <Control.Column size={[0,70,0,0]}>
                <Control.Box>
                  <Control.Print.Term value={`<p>I consent to ${companyName} for the secure electronic use and storage of my health information in accordance with applicable health privacy laws, and to related communications using my contact details. Any changes in my family group must be reported to the store.</p><p>I agree to all the terms and conditions mentioned above.</p>`} />
                </Control.Box>
              </Control.Column>
              <Control.Column size={[0,5,0,0]}><></></Control.Column>
              <Control.Column size={[0,25,0,0]} align={Code.Align.RIGHT}>
                <Control.Box>
                  <Field.Total title={Title.Gross} value={releaseData.release_gross_amount} />
                  <Field.Total title={Title.Discount} value={releaseData.release_discount_amount} />
                  <Field.Total title={Title.GST} value={releaseData.release_tax_amount} />
                  <Field.Total title={Title.Payable} value={releaseData.release_payable_amount} borderBottom={true} />
                </Control.Box>
              </Control.Column>
            </Control.Flex>

            <Field.PrintedOn fixed={false} />
        </Control.Print.Data>    
      </>)}
    {showMenu && (<div ref={contextMenuRef}><Control.PrintContextMenu keyId={keyId} showMenu={showMenu} formName={formName}  setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} /></div>)}
    <Actions.Print />
    </Control.Print.Frame>
  );
};

export default ReleasePrint;